import {FunctionComponent} from 'react';
import {FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';
import {AdvisorProfileFormValues} from '@/components/advisor-profile-form/interfaces/form-values';
import FieldWrapper from '@/components/form/field-wrapper';
import FormControl from '@/components/form/form-control';
import Input from '@/components/form/input';
import MaskedInput from '@/components/form/masked-input';
import {FieldMetadata} from '@/interfaces/form';
import {CombinedFields} from './combined-fields';

interface AdvisorCodesFirstSectionProps {
  fieldsMetadata: {
    cons9_generalAgentCode: FieldMetadata;
    cons25_advisorCode: FieldMetadata;
    altAdvisorCode1: FieldMetadata;
    altAdvisorCode2: FieldMetadata;
  };
  getFieldMessage: (fieldId: string) => string | undefined;
  formik: Pick<FormikProps<Partial<AdvisorProfileFormValues>>, 'handleChange' | 'handleBlur' | 'values' | 'errors'>;
}

export const AdvisorCodesFirstSection: FunctionComponent<AdvisorCodesFirstSectionProps> = ({
  fieldsMetadata,
  getFieldMessage,
  formik: {handleChange, handleBlur, values, errors},
}) => {
  const {t} = useTranslation('common', {keyPrefix: 'components.advisor-profile-form'});

  return (
    <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-3">
      <CombinedFields
        labelFor="CONS25"
        data-testid="CONS9-label"
        label={values.cons16_isFundServe === 'true' ? t('label-fundserv-advisor-code') : t('label-advisor-code')}
      >
        <FormControl
          isRequired={fieldsMetadata.cons9_generalAgentCode.isRequired}
          isInError={!!errors.cons9_generalAgentCode}
          errors={[errors.cons9_generalAgentCode ?? '']}
          warningMessage={getFieldMessage('cons9_generalAgentCode')}
        >
          <FieldWrapper identifier="CONS9" hidden={fieldsMetadata.cons9_generalAgentCode.isHidden}>
            <MaskedInput
              data-testid="CONS9-input"
              id="CONS9"
              mask="&&&&"
              isUppercasedMask={true}
              maxLength={4}
              name="CONS9"
              onBlur={handleBlur('cons9_generalAgentCode')}
              onAccept={handleChange('cons9_generalAgentCode')}
              required
              readOnly={fieldsMetadata.cons9_generalAgentCode.isReadOnly}
              value={values.cons9_generalAgentCode || ''}
            />
          </FieldWrapper>
        </FormControl>

        <FormControl
          isRequired={fieldsMetadata.cons25_advisorCode.isRequired}
          isInError={!!errors.cons25_advisorCode}
          errors={[errors.cons25_advisorCode ?? '']}
          warningMessage={getFieldMessage('cons25_advisorCode')}
        >
          <FieldWrapper
            identifier="CONS25"
            helpDialogTitle={
              values.cons16_isFundServe === 'true' ? t('advisor-code.fundserv.title') : t('advisor-code.title')
            }
            helpDialogToggleAccessibilityLabel={t('help-button-label-accessibility')}
            helpDialogContent={t('advisor-code.content')}
            hidden={fieldsMetadata.cons25_advisorCode.isHidden}
          >
            <MaskedInput
              data-testid="CONS25-input"
              id="CONS25"
              mask="&&&&&&"
              isUppercasedMask={true}
              maxLength={6}
              name="CONS25"
              onBlur={handleBlur('cons25_advisorCode')}
              onAccept={handleChange('cons25_advisorCode')}
              required
              readOnly={fieldsMetadata.cons25_advisorCode.isReadOnly}
              value={values.cons25_advisorCode}
            />
          </FieldWrapper>
        </FormControl>
      </CombinedFields>

      <CombinedFields
        labelFor="altAdvisorCode1"
        data-testid="CONS9_12-label"
        label={t('label-advisor-alt-code', {index: 1})}
      >
        <FormControl>
          <FieldWrapper identifier="CONS9_12" hidden={fieldsMetadata.altAdvisorCode1.isHidden}>
            <Input
              data-testid="CONS9_12-input"
              id="CONS9_12"
              name="CONS9_12"
              readOnly
              value={values.cons9_generalAgentCode ?? ''}
            />
          </FieldWrapper>
        </FormControl>

        <FormControl
          isRequired={fieldsMetadata.altAdvisorCode1.isRequired}
          isInError={!!errors.altAdvisorCode1}
          errors={[errors.altAdvisorCode1 ?? '']}
          warningMessage={getFieldMessage('cons12_altAdvisorCode1')}
        >
          <FieldWrapper identifier="altAdvisorCode1" hidden={fieldsMetadata.altAdvisorCode1.isHidden}>
            <MaskedInput
              data-testid="altAdvisorCode1_input"
              id="altAdvisorCode1"
              mask="&&&&&&"
              isUppercasedMask={true}
              maxLength={6}
              name="altAdvisorCode1"
              onBlur={handleBlur('altAdvisorCode1')}
              onAccept={handleChange('altAdvisorCode1')}
              readOnly={fieldsMetadata.altAdvisorCode1.isReadOnly}
              value={values.altAdvisorCode1 || ''}
            />
          </FieldWrapper>
        </FormControl>
      </CombinedFields>

      <CombinedFields
        labelFor="altAdvisorCode1"
        data-testid="CONS9_13-label"
        label={t('label-advisor-alt-code', {index: 2})}
      >
        <FormControl>
          <FieldWrapper identifier="CONS9_13" hidden={fieldsMetadata.altAdvisorCode2.isHidden}>
            <Input
              data-testid="CONS9_13-input"
              id="CONS9_13"
              name="CONS9_13"
              readOnly
              value={values.cons9_generalAgentCode ?? ''}
            />
          </FieldWrapper>
        </FormControl>

        <FormControl
          isRequired={fieldsMetadata.altAdvisorCode2.isRequired}
          isInError={!!errors.altAdvisorCode2}
          errors={[errors.altAdvisorCode2 ?? '']}
          warningMessage={getFieldMessage('cons13_altAdvisorCode2')}
        >
          <FieldWrapper identifier="altAdvisorCode2" hidden={fieldsMetadata.altAdvisorCode2.isHidden}>
            <MaskedInput
              data-testid="altAdvisorCode2_input"
              id="altAdvisorCode2"
              mask="&&&&&&"
              isUppercasedMask={true}
              maxLength={6}
              name="altAdvisorCode2"
              onBlur={handleBlur('altAdvisorCode2')}
              onAccept={handleChange('altAdvisorCode2')}
              readOnly={fieldsMetadata.altAdvisorCode2.isReadOnly}
              value={values.altAdvisorCode2 || ''}
            />
          </FieldWrapper>
        </FormControl>
      </CombinedFields>
    </div>
  );
};
